import type { VariantProps } from "class-variance-authority"
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "@/domains/library"

//  THINGS GET LIGHTER WHEN WE HOVER
export const buttonVariants = cva(
  cn(
    "box-border flex flex-row items-center justify-center text-t100 disabled:pointer-events-none",
    "rounded-xl",
    "border border-primary-400",
    "bg-primary",
    "gap-2",
    "transition-colors",
    "hover:bg-primary-light",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-p500",
    "disabled:cursor-not-allowed disabled:border-primary-400 disabled:text-t300",
  ),
  {
    variants: {
      variant: {
        default: "default",
        primary: cn("primary", "bg-primary ", "border-primary-400"),

        neutral: cn(
          "neutral",
          "bg-p200 disabled:bg-p200-shade",
          "border-p200-light disabled:border-p200",
          "text-t100 disabled:text-t200",
          "hover:bg-p200-light",
        ),

        error: cn(
          "error",
          "bg-error-background",
          "border-error-border disabled:border-error-border/80",
          "disabled:text-[#FF8B82]",
        ),

        ["manager"]: cn(
          "border border-manager-border",
          "bg-manager-background/80",

          "hover:bg-manager-background",
          "disabled:text-manager-text",
        ),

        ["disconnected"]: cn(
          "border border-transparent",
          "bg-disconnected-background hover:bg-disconnected-background",
          "disabled:text-disconnected-text",
        ),

        ["mobile-nav-item"]: cn(
          "border border-transparent",
          "bg-transparent hover:bg-disconnected-background",
          "disabled:text-disconnected-text",
        ),

        ["percentage"]: cn(
          "border border-secondary-600",
          "bg-p200 hover:bg-p200-light",
          "disabled:text-t100/80",
          "rounded-xl",
        ),

        // Added to fix built, cant find styles in old repo
        outline: "outline",
        ghost: "border border-transparent hover:bg-accent hover:text-t100",
        link: "text-primary underline-offset-4 hover:underline",
        "link-secondary": "text-t200 underline-offset-4 hover:text-t200-light hover:underline",
      },
      size: {
        default: "h-8 gap-2 px-2.5 py-1.5 text-sm",
        sm: "h-8 gap-2 px-2.5 py-2 text-sm",
        md: "h-9 gap-2 px-3 py-2.5 text-base",
        lg: "h-10 gap-2 px-3.5 py-2.5 text-base",
        xl: "h-11 gap-2 px-4 py-4 text-base",
        fit: "h-8 w-fit gap-2 px-4 py-1.5 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export type ButtonVariantType = VariantProps<typeof buttonVariants>

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return <Comp className={cn(buttonVariants({ size, variant, className }))} ref={ref} {...props} />
  },
)
Button.displayName = "Button"
